import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ButtonGroup,
  Button,
  Modal,
  Form,
  Image
} from 'react-bootstrap';
import axios from 'axios';
import {
  faEdit,
  faTrash,
  faPlus,
  faAngleRight,
  faAngleLeft
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useLanguage } from '../contexts/LanguageContext';
import { useMode } from '../contexts/ModeContext';
import { useResto } from '../contexts/RestoContext';
import {
  setFlashMessage,
  useWebsite
} from '../contexts/WebsiteContext';
import { useAuth } from '../contexts/AuthContext';
import config from '../config/config';

const Menus = () => {
  const { t } = useTranslation();
  const { activeLanguage } = useLanguage();
  const { restoData } = useResto();
  const { isModeAdmin } = useMode();
  const { setIsLoading } = useWebsite();
  const { token } = useAuth();

  const availableLanguages =
    restoData?.availableLanguages || [];

  const { apiUrl } = config;
  const apiPath = `${apiUrl}menu`;

  const [menusByLanguage, setMenusByLanguage] =
    useState({});
  const [editModalOpen, setEditModalOpen] =
    useState(false);
  const [isAdding, setIsAdding] =
    useState(false);

  const emptyForm = useMemo(() => ({
    id: '',
    language: '',
    label: '',
    file: null
  }), []);

  const [formData, setFormData] =
    useState(emptyForm);

  /* ================= FETCH ================= */

  const fetchItems = async () => {
    if (!restoData?.website) return;

    try {
      const response = await axios.get(
        `${apiPath}?website=${restoData.website}`
      );

      const grouped = {};
      (response.data || []).forEach(menu => {
        if (!grouped[menu.language]) {
          grouped[menu.language] = [];
        }
        grouped[menu.language].push(menu);
      });

      setMenusByLanguage(grouped);

    } catch {
      setFlashMessage(
        t('ERROR_MESSAGE'),
        'error'
      );
    }
  };

  useEffect(() => {
    fetchItems();
  }, [restoData?.website]);

  /* ================= DELETE ================= */

  const handleDelete = async (id) => {
    if (!id) return;

    setIsLoading(true);

    try {
      await axios.delete(
        `${apiPath}/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      await fetchItems();
      setFlashMessage(
        t('SUCCESS_DELETED_MESSAGE'),
        'success'
      );

    } catch {
      setFlashMessage(
        t('ERROR_MESSAGE'),
        'error'
      );
    }

    setIsLoading(false);
  };

  /* ================= RANKING ================= */

  const handleRankingUpdate =
    async (direction, id) => {

      setIsLoading(true);

      try {
        await axios.put(
          `${apiPath}/${id}`,
          { ranking: direction },
          {
            headers: {
              Authorization:
                `Bearer ${token}`
            }
          }
        );

        await fetchItems();

      } catch {
        setFlashMessage(
          t('ERROR_MESSAGE'),
          'error'
        );
      }

      setIsLoading(false);
    };

  /* ================= MODAL ================= */

  const openAddModal = () => {
    setIsAdding(true);
    setFormData(emptyForm);
    setEditModalOpen(true);
  };

  const openEditModal = (menu) => {
    setIsAdding(false);
    setFormData({
      id: menu.id,
      language: menu.language,
      label: menu.label,
      file: null
    });
    setEditModalOpen(true);
  };

  const closeModal = () => {
    setEditModalOpen(false);
    setFormData(emptyForm);
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.language) return;

    setIsLoading(true);

    try {
      const data = new FormData();

      data.append('language', formData.language);
      data.append('label', formData.label);

      if (formData.file) {
        data.append('file', formData.file);
      }

      data.append(
        'website',
        restoData.website
      );

      const method = isAdding
        ? 'post'
        : 'post';

      const url = isAdding
        ? apiPath
        : `${apiPath}/${formData.id}`;

      await axios({
        method,
        url,
        data,
        headers: {
          Authorization:
            `Bearer ${token}`,
          'Content-Type':
            'multipart/form-data'
        }
      });

      await fetchItems();
      closeModal();

      setFlashMessage(
        isAdding
          ? t('SUCCESS_CREATED_MESSAGE')
          : t('SUCCESS_UPDATED_MESSAGE'),
        'success'
      );

    } catch {
      setFlashMessage(
        t('ERROR_MESSAGE'),
        'error'
      );
    }

    setIsLoading(false);
  };

  /* ================= RENDER ================= */

  return (
    <>
      {isModeAdmin && (
        <>
          {availableLanguages.map(lang => (
            <div
              key={lang}
              className="text-center background-main-color-80"
              style={{
                color: '#fff',
                padding: '10px 0',
                marginBottom: '10px'
              }}
            >
              <div style={{
                borderBottom:
                  '1px solid rgba(255,255,255,0.2)',
                margin:
                  '0 10px 10px 10px'
              }}>
                Carte(s)
                <Image
                  src={`${config.kubuniImagesUrl}general-icons/flag_${lang}_icon_24.png`}
                  className="flag"
                  alt={lang}
                />
              </div>

              <div>
                {(menusByLanguage[lang] || [])
                  .map((menu, index) => (
                    <ButtonGroup
                      key={menu.id}
                      style={{ marginLeft: 5 }}
                    >
                      <Button variant="primary"
                        onClick={() =>
                          window.open(
                            `${config.documentsUrl}menus/${menu.filename}`,
                            '_blank'
                          )
                        }
                      >
                        {menu.label}
                      </Button>

                      <Button
                        disabled={index === 0}
                        onClick={() =>
                          handleRankingUpdate(
                            'up',
                            menu.id
                          )
                        }
                      >
                        <FontAwesomeIcon
                          icon={faAngleLeft}
                        />
                      </Button>

                      <Button
                        disabled={
                          index ===
                          menusByLanguage[lang]?.length - 1
                        }
                        onClick={() =>
                          handleRankingUpdate(
                            'down',
                            menu.id
                          )
                        }
                      >
                        <FontAwesomeIcon
                          icon={faAngleRight}
                        />
                      </Button>

                      <Button
                        onClick={() =>
                          openEditModal(menu)
                        }
                      >
                        <FontAwesomeIcon
                          icon={faEdit}
                        />
                      </Button>

                      <Button
                        onClick={() =>
                          handleDelete(menu.id)
                        }
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                        />
                      </Button>

                    </ButtonGroup>
                  ))}

                <Button
                  variant="primary"
                  onClick={openAddModal}
                  style={{ margin: '0 5px' }}
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                  />
                </Button>

              </div>
            </div>
          ))}

          {/* Modal */}
          <Modal
            show={editModalOpen}
            onHide={closeModal}
          >
            <Form onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>
                  {isAdding
                    ? t('ADD')
                    : t('EDIT')}
                </Modal.Title>
              </Modal.Header>

              <Modal.Body>

                <Form.Group>
                  <Form.Label>
                    {t('LANGUAGE')}
                  </Form.Label>

                  {availableLanguages.map(lang => (
                    <label
                      key={lang}
                      style={{ marginRight: 10 }}
                    >
                      <input
                        type="radio"
                        name="language"
                        value={lang}
                        checked={
                          formData.language === lang
                        }
                        onChange={(e) =>
                          setFormData(prev => ({
                            ...prev,
                            language:
                              e.target.value
                          }))
                        }
                        style={{
                          display: 'none'
                        }}
                      />
                      <Image
                        src={`${config.kubuniImagesUrl}general-icons/flag_${lang}_icon_24.png`}
                        style={{
                          boxShadow:
                            formData.language === lang
                              ? '0 0 5px blue'
                              : 'none'
                        }}
                      />
                    </label>
                  ))}
                </Form.Group>

                <Form.Group>
                  <Form.Label>
                    {t('LABEL')}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.label}
                    onChange={(e) =>
                      setFormData(prev => ({
                        ...prev,
                        label: e.target.value
                      }))
                    }
                    required
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>
                    {t('PDF')}
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept=".pdf"
                    onChange={(e) =>
                      setFormData(prev => ({
                        ...prev,
                        file:
                          e.target.files?.[0]
                      }))
                    }
                    required={isAdding}
                  />
                </Form.Group>

              </Modal.Body>

              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={closeModal}
                >
                  {t('CANCEL')}
                </Button>

                <Button
                  type="submit"
                  variant="primary"
                >
                  {t('CONFIRM')}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>

        </>
      )}
    </>
  );
};

export default Menus;